import React, { useState, useEffect } from 'react';
import { documentsApi, tenantsApi, Document, Tenant } from '../services/api';

const DocumentsManager: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [xmlContent, setXmlContent] = useState<string>('');

  // New document form state
  const [newDocument, setNewDocument] = useState({
    tenant_id: '',
    document_type: '01', // Invoice by default
    currency_code: 'PEN',
    accounting_supplier_party: {
      party_identification: { id: '', scheme_id: '6' },
      party_name: '',
      postal_address: {
        street_name: '',
        city_name: 'Lima',
        country_subdivision: 'Lima',
        district: '150101',
        country: 'PE'
      },
      party_legal_entity: ''
    },
    accounting_customer_party: {
      party_identification: { id: '', scheme_id: '6' },
      party_name: '',
      postal_address: {
        street_name: '',
        city_name: 'Lima',
        country_subdivision: 'Lima',
        district: '150101',
        country: 'PE'
      },
      party_legal_entity: ''
    },
    invoice_lines: [{
      id: '1',
      invoiced_quantity: 1.0,
      unit_code: 'NIU',
      line_extension_amount: 100.00,
      item: {
        description: '',
        sellers_item_identification: ''
      },
      price: {
        price_amount: 100.00,
        currency_id: 'PEN'
      }
    }]
  });

  useEffect(() => {
    Promise.all([fetchDocuments(), fetchTenants()]);
  }, [selectedTenant, statusFilter, typeFilter]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (selectedTenant) params.tenant_id = selectedTenant;
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.document_type = typeFilter;

      const response = await documentsApi.getAll(params);
      setDocuments(response.data.documents || []);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch documents');
      console.error('Documents fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await tenantsApi.getAll();
      setTenants(response.data.tenants || []);
    } catch (err: any) {
      console.error('Tenants fetch error:', err);
    }
  };

  const createDocument = async () => {
    try {
      await documentsApi.create(newDocument);
      setNewDocument({
        tenant_id: '',
        document_type: '01',
        currency_code: 'PEN',
        accounting_supplier_party: {
          party_identification: { id: '', scheme_id: '6' },
          party_name: '',
          postal_address: {
            street_name: '',
            city_name: 'Lima',
            country_subdivision: 'Lima',
            district: '150101',
            country: 'PE'
          },
          party_legal_entity: ''
        },
        accounting_customer_party: {
          party_identification: { id: '', scheme_id: '6' },
          party_name: '',
          postal_address: {
            street_name: '',
            city_name: 'Lima',
            country_subdivision: 'Lima',
            district: '150101',
            country: 'PE'
          },
          party_legal_entity: ''
        },
        invoice_lines: [{
          id: '1',
          invoiced_quantity: 1.0,
          unit_code: 'NIU',
          line_extension_amount: 100.00,
          item: {
            description: '',
            sellers_item_identification: ''
          },
          price: {
            price_amount: 100.00,
            currency_id: 'PEN'
          }
        }]
      });
      setShowCreateModal(false);
      fetchDocuments();
    } catch (err: any) {
      setError(err.message || 'Failed to create document');
    }
  };

  const handleViewDocument = async (document: Document) => {
    setSelectedDocument(document);
    try {
      const response = await documentsApi.getXml(document.id);
      setXmlContent(response.data);
    } catch (err: any) {
      setXmlContent('Error loading XML content');
    }
    setShowViewModal(true);
  };

  const handleDeleteDocument = (document: Document) => {
    setSelectedDocument(document);
    setShowDeleteModal(true);
  };

  const deleteDocument = async () => {
    if (!selectedDocument) return;
    try {
      await documentsApi.delete(selectedDocument.id);
      setShowDeleteModal(false);
      setSelectedDocument(null);
      fetchDocuments();
    } catch (err: any) {
      setError(err.message || 'Failed to delete document');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'generated':
        return 'bg-blue-100 text-blue-800';
      case 'signed':
        return 'bg-green-100 text-green-800';
      case 'submitted':
        return 'bg-purple-100 text-purple-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDocumentTypeName = (type: string) => {
    switch (type) {
      case '01': return 'Factura';
      case '03': return 'Boleta';
      case '07': return 'Nota de Crédito';
      case '08': return 'Nota de Débito';
      case '09': return 'Guía de Remisión';
      default: return type;
    }
  };

  const getTotalAmount = () => {
    return documents.reduce((sum, doc) => sum + parseFloat(doc.total_amount), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading documents...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Documents Management</h2>
          <p className="mt-1 text-sm text-gray-600">Manage electronic invoices and documents</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          📄 Create Document
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="text-red-400">⚠️</div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button
                onClick={fetchDocuments}
                className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Filter by Tenant</label>
            <select
              value={selectedTenant}
              onChange={(e) => setSelectedTenant(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Tenants</option>
              {tenants.map(tenant => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.company_name} ({tenant.ruc})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Filter by Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Status</option>
              <option value="generated">Generated</option>
              <option value="signed">Signed</option>
              <option value="submitted">Submitted</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Filter by Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Types</option>
              <option value="01">Factura</option>
              <option value="03">Boleta</option>
              <option value="07">Nota de Crédito</option>
              <option value="08">Nota de Débito</option>
              <option value="09">Guía de Remisión</option>
            </select>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <span className="text-2xl">📄</span>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total Documents</p>
              <p className="text-2xl font-bold text-gray-900">{documents.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <span className="text-2xl">💰</span>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total Amount</p>
              <p className="text-2xl font-bold text-green-600">
                PEN {getTotalAmount().toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <span className="text-2xl">✅</span>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Generated</p>
              <p className="text-2xl font-bold text-blue-600">
                {documents.filter(d => d.status === 'generated').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <span className="text-2xl">🔐</span>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Signed</p>
              <p className="text-2xl font-bold text-green-600">
                {documents.filter(d => d.status === 'signed').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Documents Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">All Documents</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Document
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {documents.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    <div className="text-4xl mb-2">📄</div>
                    <p>No documents found</p>
                    <p className="text-sm">Create your first electronic document</p>
                  </td>
                </tr>
              ) : (
                documents.map((document) => (
                  <tr key={document.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{document.document_number}</p>
                        <p className="text-sm text-gray-500">Series: {document.series}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getDocumentTypeName(document.document_type)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(document.status)}`}>
                        {document.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {document.currency_code} {parseFloat(document.total_amount).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(document.issue_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleViewDocument(document)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        View XML
                      </button>
                      <button
                        onClick={() => handleDeleteDocument(document)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Document Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white max-h-screen overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Document</h3>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tenant</label>
                    <select
                      value={newDocument.tenant_id}
                      onChange={(e) => setNewDocument({ ...newDocument, tenant_id: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Tenant</option>
                      {tenants.map(tenant => (
                        <option key={tenant.id} value={tenant.id}>
                          {tenant.company_name} ({tenant.ruc})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Document Type</label>
                    <select
                      value={newDocument.document_type}
                      onChange={(e) => setNewDocument({ ...newDocument, document_type: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="01">Factura</option>
                      <option value="03">Boleta</option>
                      <option value="07">Nota de Crédito</option>
                      <option value="08">Nota de Débito</option>
                      <option value="09">Guía de Remisión</option>
                    </select>
                  </div>
                </div>

                {/* Supplier Information */}
                <div className="border-t pt-4">
                  <h4 className="text-md font-medium text-gray-900 mb-2">Supplier Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">RUC</label>
                      <input
                        type="text"
                        value={newDocument.accounting_supplier_party.party_identification.id}
                        onChange={(e) => setNewDocument({
                          ...newDocument,
                          accounting_supplier_party: {
                            ...newDocument.accounting_supplier_party,
                            party_identification: { ...newDocument.accounting_supplier_party.party_identification, id: e.target.value }
                          }
                        })}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="20123456789"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Company Name</label>
                      <input
                        type="text"
                        value={newDocument.accounting_supplier_party.party_name}
                        onChange={(e) => setNewDocument({
                          ...newDocument,
                          accounting_supplier_party: { ...newDocument.accounting_supplier_party, party_name: e.target.value }
                        })}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Company Name"
                      />
                    </div>
                  </div>
                </div>

                {/* Customer Information */}
                <div className="border-t pt-4">
                  <h4 className="text-md font-medium text-gray-900 mb-2">Customer Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">RUC/DNI</label>
                      <input
                        type="text"
                        value={newDocument.accounting_customer_party.party_identification.id}
                        onChange={(e) => setNewDocument({
                          ...newDocument,
                          accounting_customer_party: {
                            ...newDocument.accounting_customer_party,
                            party_identification: { ...newDocument.accounting_customer_party.party_identification, id: e.target.value }
                          }
                        })}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="20100000001"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Customer Name</label>
                      <input
                        type="text"
                        value={newDocument.accounting_customer_party.party_name}
                        onChange={(e) => setNewDocument({
                          ...newDocument,
                          accounting_customer_party: { ...newDocument.accounting_customer_party, party_name: e.target.value }
                        })}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Customer Name"
                      />
                    </div>
                  </div>
                </div>

                {/* Invoice Line */}
                <div className="border-t pt-4">
                  <h4 className="text-md font-medium text-gray-900 mb-2">Product/Service</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Description</label>
                      <input
                        type="text"
                        value={newDocument.invoice_lines[0].item.description}
                        onChange={(e) => setNewDocument({
                          ...newDocument,
                          invoice_lines: [{
                            ...newDocument.invoice_lines[0],
                            item: { ...newDocument.invoice_lines[0].item, description: e.target.value }
                          }]
                        })}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Product or service description"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Amount (PEN)</label>
                      <input
                        type="number"
                        value={newDocument.invoice_lines[0].line_extension_amount}
                        onChange={(e) => {
                          const amount = parseFloat(e.target.value) || 0;
                          setNewDocument({
                            ...newDocument,
                            invoice_lines: [{
                              ...newDocument.invoice_lines[0],
                              line_extension_amount: amount,
                              price: { ...newDocument.invoice_lines[0].price, price_amount: amount }
                            }]
                          });
                        }}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="100.00"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end pt-6 space-x-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={createDocument}
                  disabled={!newDocument.tenant_id || !newDocument.accounting_supplier_party.party_name || !newDocument.invoice_lines[0].item.description}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  Create Document
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Document Modal */}
      {showViewModal && selectedDocument && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white max-h-screen overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Document XML: {selectedDocument.document_number}
              </h3>

              <div className="bg-gray-100 rounded-lg p-4 mb-4">
                <pre className="text-sm overflow-x-auto whitespace-pre-wrap">
                  {xmlContent}
                </pre>
              </div>

              <div className="flex items-center justify-end">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Document Modal */}
      {showDeleteModal && selectedDocument && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
                <span className="text-red-600 text-2xl">⚠️</span>
              </div>

              <h3 className="text-lg font-medium text-gray-900 text-center mt-4 mb-4">
                Delete Document
              </h3>

              <p className="text-sm text-gray-500 text-center mb-4">
                Are you sure you want to delete document "{selectedDocument.document_number}"?
                This action cannot be undone.
              </p>

              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={deleteDocument}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Delete Document
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsManager;